import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, MapPinned, Vote, PiggyBank, SearchCheck, RadioTower } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { PageHeader, SectionHeader, Loading, RichSection } from '../components/Shared';
import { HopeInlineSection, useHopeTheme } from '../components/HopeTheme';
import { useCampaignContent, TaxPromiseSection, ReportTrackBlock } from '../components/CampaignBlocks';
import { FlyerDownloadSection } from '../components/FlyerDownload';
import { CvDownloadSection } from '../components/CvDownload';

// The six platform areas (user-approved Phase B structure)
const PLATFORM_AREAS = [
  { id: 'accountability', icon: ShieldCheck, title: 'Accountability', body: 'Open information, honest answers, and systems that let residents see their City working. Not promises on paper: tools you can check yourself.', label: 'How It Works', url: '/start-here#why-im-running' },
  { id: 'pita', icon: MapPinned, title: 'PITA: Your Issues, Tracked', body: 'The Ward 8 Public Issue Tracker App. Report It. Map It. Track It. Get Answers. One complaint can be overlooked. A visible pattern cannot.', label: 'Open PITA', url: '/issue-tracker' },
  { id: 'your-vote', icon: Vote, title: 'Paper Ballots / Witness Your Vote', body: 'Restore the paper ballot choice before the 2026 election. And if municipalities continue without paper ballots, Witness Your Vote adds transparency.', label: 'Paper Ballots', url: '/paper-ballots' },
  { id: 'lower-taxes', icon: PiggyBank, title: 'Lower Taxes Through Responsible Spending', body: 'Cut the cost of government before asking taxpayers for more. Find waste, review management costs, and protect essential services.', label: 'The Plan', url: '/lower-taxes' },
  { id: 'forensic-audit', icon: SearchCheck, title: 'Independent Forensic Audit', body: 'An independent, professional review of how the City spends money, with findings reported publicly to the people who pay the bills.', label: 'Why an Audit', url: '/forensic-audit' },
  { id: 'media', icon: RadioTower, title: 'Media for the People', body: 'Do not make residents hunt for important public information. Bring the information to them: decisions, notices, and taxpayer facts.', label: 'The Vision', url: '/media-for-the-people' },
];

export default function Platform() {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const hope = useHopeTheme();
  const campaign = useCampaignContent();

  useEffect(() => {
    api.get('/pages/platform').then((r) => setPage(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;
  const pillars = page?.pillars || [];
  const scrollToPillar = (n) => {
    const el = document.getElementById(`commitment-${n}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="platform-page">
      <PageHeader pageKey="platform" kicker="MY PLATFORM" title="ACCOUNTABILITY YOU CAN SEE." subtitle="I promise accountability by building the system for it." />
      <RichSection id="platform-sections.intro" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8" />

      {/* The fourteen commitments, in the order of the site structure document (CMS-managed) */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <SectionHeader kicker="THE PLATFORM" title={`${pillars.length === 14 ? 'Fourteen' : pillars.length} commitments, in order`} dek="Every commitment connects to something residents can watch, check, and question. Open any heading to read it in full." />
          <HopeInlineSection block={hope.platform} testid="hope-platform" />
          {/* Quick index */}
          <ol className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mb-8 text-sm" data-testid="platform-index">
            {pillars.map((p) => (
              <li key={p.id}>
                <button type="button" onClick={() => scrollToPillar(p.number)} className="text-left w-full inline-flex items-baseline gap-2 py-1 text-[#1a2744] hover:underline decoration-[#C9A84C] decoration-2 underline-offset-4 min-h-[32px]" data-testid={`platform-index-${p.number}`}>
                  <span className="font-black text-[#24406f] w-6 shrink-0 tabular-nums">{p.number}.</span><span className="font-semibold">{p.title}</span>
                </button>
              </li>
            ))}
          </ol>
          <Accordion type="multiple" defaultValue={pillars.slice(0, 1).map((p) => p.id)} className="space-y-3" data-testid="platform-pillars-accordion">
            {pillars.map((p) => (
              <AccordionItem key={p.id} value={p.id} id={`commitment-${p.number}`} className="bg-white rounded-xl border border-slate-200 px-5 data-[state=open]:border-[#24406f] scroll-mt-24" data-testid="platform-pillar">
                <AccordionTrigger className="hover:no-underline py-5" data-testid={`platform-pillar-trigger-${p.number}`}>
                  <div className="flex items-center gap-4 text-left">
                    <span className="inline-flex items-center justify-center h-9 w-9 shrink-0 rounded-full bg-[#1a2744] text-white font-black">{p.number}</span>
                    <span className="font-serif font-bold text-[#1a2744] text-base sm:text-lg leading-snug">{p.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-5 pl-0 sm:pl-13">
                  <p className="text-slate-700 leading-relaxed sm:ml-13">{p.body}</p>
                  {p.link && (
                    <Link to={p.link} className="inline-flex items-center gap-1 mt-3 sm:ml-13 text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid={`platform-pillar-link-${p.number}`}>{p.link_label || 'Read more'} <ArrowRight className="h-3.5 w-3.5" /></Link>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

      {/* The six platform areas (preserved): the tools behind the commitments */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14" data-testid="platform-areas-section">
        <SectionHeader kicker="THE TOOLS" title="Six ways you will see it working" dek="The systems behind the commitments: each one is something residents can open, use and check." />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PLATFORM_AREAS.map(({ id, icon: Icon, title, body, label, url }) => (
            <Card key={id} className="card-lift" data-testid={`platform-area-${id}`}>
              <CardContent className="p-5 flex flex-col h-full">
                <div className="h-10 w-10 rounded-lg bg-[#1a2744] flex items-center justify-center mb-3">
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <p className="font-serif font-bold text-[#1a2744] leading-snug">{title}</p>
                <p className="text-sm text-slate-600 mt-1.5 leading-relaxed flex-1">{body}</p>
                <Button asChild className="mt-4 h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press w-full" data-testid={`platform-area-button-${id}`}>
                  <Link to={url}>{label} <ArrowRight className="h-4 w-4 ml-1" /></Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <RichSection id="platform-sections.closing" className="mb-6" />
        <div className="bg-[#0b0f17] rounded-2xl p-6 sm:p-8 text-center">
          <p className="font-serif font-black text-white text-xl sm:text-2xl">Steve promises accountability by building the system for it.</p>
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            <Button asChild className="h-12 bg-white hover:bg-slate-100 text-[#1a2744] font-bold btn-press" data-testid="platform-hub-button"><Link to="/issue-tracker">See the Tools <ArrowRight className="h-4 w-4 ml-1" /></Link></Button>
            <Button asChild className="h-12 bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold btn-press"><Link to="/volunteer">Volunteer</Link></Button>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <FlyerDownloadSection loc="platform" />
          <CvDownloadSection loc="platform" />
        </div>
      </div>

      {/* The Tax Promise (existing campaign commitment, preserved) */}
      {campaign?.tax_promise?.sections?.platform_tax_promise !== false && <TaxPromiseSection promise={campaign?.tax_promise} />}

      {/* Report It. Track It. Cut the Waste. Lower the Taxes. */}
      <ReportTrackBlock promise={campaign?.tax_promise} />
    </div>
  );
}
